import { create } from 'zustand';

export type ToastType = 'success' | 'error' | 'info' | 'warn';

export interface Toast {
  id: string;
  message: string;
  type: ToastType;
  ttl: number;
  createdAt: number;
}

interface ToastState {
  toasts: Toast[];
  show: (message: string, type?: ToastType, ttl?: number) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

let counter = 0;
function nextId(): string {
  counter += 1;
  return `t_${Date.now().toString(36)}_${counter}`;
}

export const useToastStore = create<ToastState>((set, get) => ({
  toasts: [],
  show: (message, type = 'info', ttl = 4000) => {
    const id = nextId();
    const toast: Toast = { id, message, type, ttl, createdAt: Date.now() };
    set((s) => ({ toasts: [...s.toasts, toast] }));
    if (ttl > 0 && typeof window !== 'undefined') {
      window.setTimeout(() => {
        get().dismiss(id);
      }, ttl);
    }
    return id;
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
  clear: () => set({ toasts: [] }),
}));

/**
 * Imperative helper, usable from anywhere (event handlers, mutations, etc).
 */
export const toast = {
  success: (msg: string, ttl?: number) => useToastStore.getState().show(msg, 'success', ttl),
  error: (msg: string, ttl?: number) => useToastStore.getState().show(msg, 'error', ttl ?? 6000),
  info: (msg: string, ttl?: number) => useToastStore.getState().show(msg, 'info', ttl),
  warn: (msg: string, ttl?: number) => useToastStore.getState().show(msg, 'warn', ttl),
  dismiss: (id: string) => useToastStore.getState().dismiss(id),
};

export default toast;
